import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Outlet, createFileRoute, useMatchRoute } from "@tanstack/react-router";
import { useEffect } from "react";

import { BackButton } from "@ando/ui";

import { useTakeoverBack } from "@/lib/takeoverOrigin";
import { createContext, useState } from "react";

/**
 * Punto di aggancio a destra della barra del back.
 *
 * Quella riga e' alta 44px e ha il lato destro completamente vuoto: le aree che
 * hanno un controllo globale (il range temporale di Admin) lo mettono li invece
 * di aprirsi una fascia propria sotto. Passa un ELEMENTO, non una prop, cosi il
 * guscio condiviso non deve sapere nulla di chi lo riempie — Impostazioni e
 * Integrazioni non lo usano e restano identiche.
 *
 * `ref={setNode}` e' una callback ref legata allo stato: il consumatore ottiene
 * il nodo appena esiste, senza effetti e senza cicli di render.
 */
export const ModalBarSlotContext = createContext<HTMLElement | null>(null);

/**
 * ``_authed/_modal`` — full-screen takeover layout group (May 22, 2026).
 *
 * Why a separate layout: dense admin tables (Org detail w/ 10 tabs, Users
 * page w/ 622 LoC of drill-down, Engagement cohort grids) need ≥1100px to
 * read comfortably. The split-shell (38% chat + 62% workspace) gives them
 * ~890px on a 1440 viewport — exactly the "fatto malissimo" complaint
 * audit traced back to.
 *
 * What this layout does NOT render:
 *   - the 38% chat pane
 *   - the ``WorkspaceTabs`` (Conversazioni / Calendario / Finanza / …)
 *   - the ``SourceDrawerSurface``
 *   - the top-left ``ProjectPill`` (context belongs to workspace, not admin)
 *
 * What it DOES render:
 *   - top chrome strip: "← Torna al workspace" breadcrumb (left), ESC hint
 *     + close X (right). Sticky at top so it survives table scroll.
 *   - full-width Outlet body — surface owns its own scroll.
 *   - a backdrop blur underneath so the visual reset is clean (no peek-
 *     through of half-visible chat tokens streaming).
 *
 * Keyboard:
 *   - ``ESC`` returns to ``/workspace/conversations`` (the default authed
 *     surface — matches the URL the avatar drawer assumes).
 *
 * Auth: inherits the ``_authed`` ``beforeLoad`` redirect, so anonymous
 * users still bounce to ``/login`` before this layout mounts.
 */
export const Route = createFileRoute("/_authed/_modal")({
  component: ModalShell,
});

function ModalShell() {
  const { i18n } = useLingui();
  const matchRoute = useMatchRoute();

  // The Platform Console is a STANDALONE full-screen surface with its own
  // topbar (org identity, theme toggle, user menu) — rendering the modal
  // chrome strip on top of it produced a double header ("Torna al
  // workspace" + console topbar). Console routes get a bare takeover;
  // navigation back to the product lives inside the console shell itself.
  // ``matchRoute`` restituisce ``false`` quando non matcha, MAI ``null``. Con
  // ``!= null`` il confronto era sempre vero (``false != null`` → true), quindi
  // ogni takeover prendeva il ramo "chrome propria": niente barra indietro,
  // niente sfondo opaco (i BrandBlobs trasparivano), ESC morto.
  const [barSlot, setBarSlot] = useState<HTMLElement | null>(null);
  const isConsole = matchRoute({ to: "/console", fuzzy: true }) !== false;

  // Settings surfaces (Jul 27, 2026) render their OWN in-flow back bar in
  // ``_modal/settings/route.tsx`` (SettingsShell) — the ModalShell's own top
  // bar kept not showing for the user on these routes. So, like the console,
  // settings get a bare takeover here and own their back nav; this guarantees
  // exactly ONE back bar (the in-flow one, impossible to hide).
  const isSettings = matchRoute({ to: "/settings", fuzzy: true }) !== false;

  // Integrations (Aug 25, 2026) followed Settings for the same reason: the
  // ModalShell bar did not show up on the hub, so the fullscreen takeover had
  // no visible way back. ``_modal/integrations/route.tsx`` paints its own
  // in-flow back bar — skip ours here so there is never a double bar. The hub
  // does not use the bar slot below, so nothing is lost by staying bare.
  const isIntegrations = matchRoute({ to: "/integrations", fuzzy: true }) !== false;

  // Admin (Aug 26, 2026) grew an in-flow back control for the same "the shared
  // bar never showed up" reason, but it is deliberately NOT listed here. That
  // reason was the ``!= null`` comparison fixed above: it made EVERY takeover
  // take the bare branch, so no surface ever saw the shared bar. Now that the
  // bar renders, Admin needs it — ``_modal/admin/route.tsx`` portals
  // ``AdminRangePicker`` into ``ModalBarSlotContext``, and that slot node only
  // exists on the shared bar. Adding ``isAdmin`` back would leave the slot
  // null and silently delete the global time-range filter from every
  // range-aware Admin section. Admin's own in-flow back control is now
  // redundant and should be dropped from that file.
  const ownsOwnChrome = isConsole || isSettings || isIntegrations;

  // Il ritorno va all'ULTIMA superficie di workspace visitata, non a una
  // rotta fissa: la #67 ha unificato qui il controllo "indietro" e frd
  // aveva gia' la memoria dell'origine (``useTakeoverBack``), quindi la
  // barra condivisa la usa e nessuno dei due comportamenti si perde.
  // ``readTakeoverOrigin`` ha un fallback e rifiuta href fuori dal workspace.
  const close = useTakeoverBack();

  // ESC handler — global, mounted only while the modal layout is alive.
  // Not for the console: a console-only user has no workspace to go back
  // to, and the console owns its own navigation.
  useEffect(() => {
    if (isConsole) return;
    const onKey = (e: KeyboardEvent): void => {
      // A surface that used ESC itself (e.g. leaving the deck editor's full
      // screen) marks it handled.
      if (e.key !== "Escape" || e.defaultPrevented) return;
      // Don't hijack ESC when a Radix Dialog (confirm modal, drawer) is
      // already open inside the admin surface — those own their own ESC
      // semantics. ``data-state="open"`` is the Radix marker.
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close, isConsole]);

  // Full-screen takeover. ``_authed`` no longer mounts the workspace shell
  // behind a modal route (see its ``isModalRoute`` gate), so this ``fixed
  // inset-0`` layer has nothing behind it to bleed through and needs no
  // portal — it simply owns the viewport. The non-console variant renders
  // the "← Torna al workspace" chrome strip; the console paints its own bg
  // + back nav ("Torna ad Ando") inside ConsoleShell.
  if (ownsOwnChrome) {
    return (
      <div className="fixed inset-0 z-40 flex flex-col">
        <Outlet />
      </div>
    );
  }

  // The back affordance is rendered INLINE here (was a separate
  // ``ModalChromeStrip`` component that, for reasons that never reproduced in
  // the code, rendered EMPTY on the settings/integrations surfaces — the
  // takeover was opaque but had no visible "← back"). This single bar lives
  // in the shared modal layout, so EVERY takeover surface (Settings, Brand
  // Kit, Integrations, Admin …) gets the same back control — never page by
  // page. Deliberately plain: solid bg, a real border, an explicit height,
  // no ``sticky`` / ``backdrop-blur`` (which can collapse or blank a bar that
  // has nothing behind it in a full-screen fixed layer).
  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-cream">
      {/* Back bar — the ONLY way out of a full-screen settings/integrations
          takeover. Era una PILLOLA bordata perché "text-on-cream era
          invisibile": quella diagnosi è caduta insieme al bug qui sopra —
          la barra non veniva renderizzata affatto, e sopra i BrandBlobs
          senza fondo opaco QUALSIASI cosa sarebbe stata illeggibile. Su
          crema piatto `ink-2` sta oltre 7:1, quindi il ritorno può essere
          tipografia (`BackButton`) invece di un box. Resta vero che una
          pillola urla di più: se un domani qualcuno non lo trova, la leva
          è il contrasto del testo, non rimettere il bordo. */}
      <div className="flex h-11 shrink-0 items-center border-hairline border-b px-4">
        <BackButton onClick={close} label={i18n._(msg`Torna al workspace`)} />
        <div ref={setBarSlot} className="ml-auto flex items-center" />
      </div>
      {/* Niente `role="region"`: <main> E' GIA' un landmark, e sovrascriverlo
          lo declassa a regione generica facendolo sparire dalla navigazione per
          landmark degli screen reader. L'`aria-label` su <main> resta valido e
          gli da' il nome. */}
      <main
        className="min-h-0 flex-1 overflow-hidden"
        aria-label={i18n._(msg`Area amministrativa a schermo intero`)}
      >
        <ModalBarSlotContext.Provider value={barSlot}>
          <Outlet />
        </ModalBarSlotContext.Provider>
      </main>
    </div>
  );
}

// The back bar is now inlined in ModalShell above (it previously lived in a
// separate ``ModalChromeStrip`` component that rendered empty on some
// takeover surfaces). One shared bar ⇒ every settings/integrations page gets
// the same "← Torna al workspace" affordance.
